// Pure helper that merges static seat geometry (instanceSeats) with the dynamic
// selection/availability state into the array the seat plan renders.
//
// It caches each merged seat object keyed on its *static* seat object, returning
// the SAME reference whenever the dynamic state is unchanged. That stable
// identity is what lets React.memo(Seat) skip unchanged seats — so a click
// re-renders one seat instead of the whole ~1,500-seat plan. The cache is a
// WeakMap keyed on the static seat objects, so entries are GC'd with the
// instance and never leak between instances.
//
// Kept dependency-free (no ibiza / lib aliases) so it is unit-testable in jest.
const seatMergeCache = new WeakMap()

// Resolve the real seat id from a model-method's `arguments`.
//
// Ibiza may invoke a model method with the model proxy injected as the first
// argument, so the real id can arrive as args[0] OR args[1] (this is why
// handleClick/handleRemoveTicket each carry an inline guard). Returning a clean
// String id — or null when no usable id is present — keeps seatActions keyed by
// id and never by a stringified object ("[object Object]").
export function resolveSeatId (args) {
  let id = args[0]
  if (typeof id === 'object' && id !== null && args.length > 1) {
    id = args[1]
  }
  if (id == null || typeof id === 'object' || typeof id === 'function') {
    return null
  }
  return String(id)
}

export function buildSeatList ({ instanceSeats, seatActions, availableSeats, currentBasket }) {
  if (!instanceSeats) return []

  // Only consider we have availability if availableSeats is set (not null/undefined)
  const hasAvailability = availableSeats !== null && availableSeats !== undefined

  const basketSeatIds = new Map()
  if (currentBasket && currentBasket.length > 0) {
    currentBasket.forEach(t => {
      if (t.seat?.id) basketSeatIds.set(t.seat.id, t)
    })
  }

  return instanceSeats.map(seat => {
    const seatId = String(seat.id)
    const seatAction = seatActions?.[seatId]
    const basketItem = basketSeatIds.get(seat.id)
    const alreadyInBasket = !!basketItem
    const markedForRemoval = !!(basketItem && seatAction?.removeFromBasket)
    const inBasket = !!(seatAction?.addToBasket || alreadyInBasket)

    // Simplified availability check
    let available = false
    if (!inBasket) {
      if (!hasAvailability) {
        available = false // Don't show as available while loading
      } else {
        const status = availableSeats[seatId]
        // If no status exists for this seat, it's available
        // If status exists, check if nok array is empty
        available = !status || !status.nok || status.nok.length === 0
      }
    }

    const loading = !hasAvailability

    // Reuse the cached merged object when nothing the UI reads has changed, so
    // its reference identity is stable across renders (React.memo(Seat) holds).
    const signature = `${inBasket}|${alreadyInBasket}|${markedForRemoval}|${available}|${loading}`
    const cached = seatMergeCache.get(seat)
    if (cached && cached.signature === signature) {
      return cached.merged
    }

    const merged = {
      ...seat,
      inBasket,
      alreadyInBasket,
      markedForRemoval,
      available,
      loading
    }
    seatMergeCache.set(seat, { signature, merged })
    return merged
  })
}
